import { useEffect, useMemo, useState } from 'react';
import type { MailcomAccountsResponse, WireProviderAccount } from '../../api/endpoints.js';
import type { useApiQuery } from '../../api/hooks.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
} from '../../ui/index.js';
import { ParentMailsTable } from './ParentMailsTable.js';
import { supportsOperatorOffSale } from './parentSaleApi.js';
import { TakeOffSaleDialog } from './TakeOffSaleDialog.js';

// R-D214 — V1-parity status taxonomy. Status-bucket tokens map to the
// 5-bucket DB enum; taxonomy tokens (alias_limit_reached / renewal_failed /
// renewal_in_progress / upstream_unavailable / blocked / bootstrapping)
// land on status_detail or suspended_reason inside the client (see
// listMailcomAccounts in endpoints.ts).
type StatusFilter =
  | 'all'
  | 'active'
  | 'draining'
  | 'suspended'
  | 'login_failed'
  | 'disabled'
  | 'alias_limit_reached'
  | 'renewal_failed'
  | 'renewal_in_progress'
  | 'upstream_unavailable'
  | 'blocked'
  | 'bootstrapping'
  | 'phone_verification_required'
  // 2026-09-23 (D-239) — status_detail tokens (routed onto statusDetail).
  | 'operator_off_sale'
  | 'imap_rejected';

interface AccountsGridProps {
  readonly providerId: string;
  readonly query: ReturnType<typeof useApiQuery<MailcomAccountsResponse>>;
  readonly canManage: boolean;
  readonly onOpenAliases: (accountId: string) => void;
  readonly onOpenRenewals: (accountId: string) => void;
  readonly onOpenCredential: (accountId: string) => void;
  readonly onAfterAction: () => void;
  readonly page: number;
  readonly onPageChange: (n: number) => void;
  readonly search: string;
  readonly onSearchChange: (s: string) => void;
  readonly statusFilter: StatusFilter;
  readonly onStatusFilterChange: (s: StatusFilter) => void;
  readonly pageSize: number;
}

export function AccountsGrid({
  providerId,
  query,
  canManage,
  onOpenAliases,
  onOpenRenewals,
  onOpenCredential,
  onAfterAction,
  page,
  onPageChange,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  pageSize,
}: AccountsGridProps): JSX.Element {
  const [searchDraft, setSearchDraft] = useState(search);
  const accounts = query.kind === 'ok' ? query.data?.accounts ?? [] : [];
  const pagination = query.kind === 'ok' ? query.data?.pagination : undefined;

  // 2026-09-23 (D-239) — bulk "Satıştan çek" over the selected rows (only
  // where the server serves it — supportsOperatorOffSale, gmx today). A new page / filter / search starts a new selection; only
  // rows still on this page and not disabled count (a poll may change them).
  const selectionSupported = canManage && supportsOperatorOffSale(providerId);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkTargets, setBulkTargets] = useState<readonly WireProviderAccount[] | null>(null);
  useEffect(() => {
    setSelected(new Set());
  }, [page, search, statusFilter, providerId]);
  const selectedTargets = useMemo(
    () => accounts.filter((a) => a.status !== 'disabled' && selected.has(a.id)),
    [accounts, selected],
  );

  return (
    <Card flush className="mcp-accounts" data-testid="mailcom-accounts-section">
      <CardHeader
        title="Parent Mail Listesi"
        actions={
          pagination ? (
            <span className="t-tertiary t-small">
              Toplam: <strong style={{ color: 'var(--text-primary)' }}>{pagination.total.toLocaleString('tr-TR')}</strong>{' '}
              · sayfa {pagination.page} / {pagination.pages}
            </span>
          ) : null
        }
      />
      <CardBody>
        <form
          className="mcp-filters"
          style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}
          data-testid="mailcom-account-filters"
          onSubmit={(ev) => {
            ev.preventDefault();
            onSearchChange(searchDraft.trim());
          }}
        >
          <div className="mcp-filters-search" style={{ flex: 1, minWidth: 240 }}>
            <Input
              type="search"
              placeholder="E-posta / handle ara…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              aria-label="parent e-posta ara"
              data-testid="filter-email"
              iconLeft={<Icon.Search size={14} />}
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as StatusFilter)}
            aria-label="durum filtresi"
            data-testid="filter-status"
            className="mcp-filters-status"
            style={{ width: 220 }}
          >
            <option value="all">Tüm durumlar</option>
            {/* Status buckets (DB enum) */}
            <option value="active">Aktif</option>
            <option value="draining">Draining</option>
            <option value="suspended">Askıda</option>
            <option value="login_failed">Giriş Hatası</option>
            <option value="disabled">Devre Dışı</option>
            {/* R-D214 V1-taxonomy tokens (client routes onto statusDetail /
                suspendedReason inside listMailcomAccounts). */}
            <option value="renewal_in_progress">Yenileniyor</option>
            <option value="renewal_failed">Yenileme Hatası</option>
            <option value="alias_limit_reached">Alias Limiti (Retry)</option>
            <option value="upstream_unavailable">Upstream Erişilemez</option>
            <option value="blocked">Engellendi</option>
            <option value="bootstrapping">Bootstrapping</option>
            {/* 2026-09-22 — GMX-only status_detail token (phone wall). */}
            {providerId === 'gmx' ? (
              <option value="phone_verification_required">Telefon doğrulaması</option>
            ) : null}
            {/* 2026-09-23 (D-239) — operator take-off-sale (where the server
                serves it) and the GMX IMAP quarantine. */}
            {supportsOperatorOffSale(providerId) ? (
              <option value="operator_off_sale">Satıştan çekildi (operatör)</option>
            ) : null}
            {providerId === 'gmx' ? (
              <option value="imap_rejected">IMAP girişi reddedildi</option>
            ) : null}
          </Select>
          <Button type="submit" variant="primary" className="mcp-filters-submit" icon={<Icon.Search size={14} />}>Ara</Button>
        </form>

        {query.kind === 'loading' ? <LoadingState /> : null}
        {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={onAfterAction} /> : null}
        {query.kind === 'ok' ? (
          accounts.length === 0 ? (
            <EmptyState
              label={
                search.length > 0 || statusFilter !== 'all'
                  ? 'Filtrelerle eşleşen parent mail bulunamadı.'
                  : 'Henüz parent mail hesabı yok.'
              }
              hint={
                search.length > 0 || statusFilter !== 'all'
                  ? 'E-posta arama veya durum filtresini değiştirip tekrar deneyin.'
                  : 'Üstteki "Yeni Parent" veya "Import / TXT" butonuyla mail.com hesabı kaydedebilirsiniz.'
              }
              icon={<Icon.Mail size={22} />}
            />
          ) : (
            <>
              {selectionSupported && selectedTargets.length > 0 ? (
                <div
                  className="mcp-bulkbar"
                  data-testid="parents-bulk-bar"
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    padding: '8px 12px',
                    marginBottom: 10,
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 6,
                    background: 'var(--bg-elevated)',
                  }}
                >
                  <span className="t-small mcp-bulkbar-count">
                    <strong>{selectedTargets.length}</strong> parent seçili
                  </span>
                  <Button
                    size="sm"
                    variant="danger"
                    icon={<Icon.Cancel size={13} />}
                    onClick={() => setBulkTargets(selectedTargets)}
                    data-testid="parents-bulk-take-off-sale"
                  >
                    Seçilenleri satıştan çek
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                    Seçimi temizle
                  </Button>
                </div>
              ) : null}
              <ParentMailsTable
                providerId={providerId}
                accounts={accounts}
                canManage={canManage}
                onOpenAliases={onOpenAliases}
                onOpenRenewals={onOpenRenewals}
                onOpenCredential={onOpenCredential}
                onAfterAction={onAfterAction}
                {...(selectionSupported ? { selectedIds: selected, onSelectionChange: setSelected } : {})}
              />
            </>
          )
        ) : null}
        {/* The targets are frozen when the dialog opens, so the result summary
            survives the post-action refresh that clears the selection. */}
        <TakeOffSaleDialog
          providerId={providerId}
          open={bulkTargets !== null}
          mode="bulk"
          targets={bulkTargets ?? []}
          onClose={() => setBulkTargets(null)}
          onDone={() => {
            setSelected(new Set());
            onAfterAction();
          }}
        />

        {pagination && pagination.pages > 1 ? (
          <div
            className="mcp-pager"
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 16,
              paddingTop: 12,
              borderTop: '1px solid var(--border-subtle)',
            }}
            data-testid="mailcom-accounts-pagination"
          >
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              icon={<Icon.ChevronLeft size={14} />}
            >
              Önceki
            </Button>
            <span className="t-tertiary t-small mcp-pager-label">
              <span className="mcp-pager-page">Sayfa {pagination.page} / {pagination.pages}</span>
              <span className="mcp-pager-per">
                <span className="mcp-pager-sep"> · </span>sayfa başına {pageSize}
              </span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= pagination.pages}
              onClick={() => onPageChange(page + 1)}
              iconRight={<Icon.ChevronRight size={14} />}
            >
              Sonraki
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
